import { RENEW_TOKEN_INTERVAL_MILLISECONDS } from '../../store/ducks/auth/types';
import {
  ECustomerTypeView,
  EResearchNps,
  IUser,
  IUserPreference
} from '../../store/ducks/user/types';
import {
  customersAuthorizationApi,
  ECustomerAuthorizationEndpoints
} from '../api';
import { RefreshAuthTokenService } from '../Auth';
import { EGerdauService } from '../EGerdauService';

export interface IUserService {
  getUser: (userId: string) => Promise<IUser>;
}

type SurveyProps = {
  status: EResearchNps;
  rating?: string;
  surveyText?: string | undefined;
};

export class UserService extends EGerdauService implements IUserService {
  async validate(): Promise<IUser> {
    // const refreshAuthTokenService = new RefreshAuthTokenService();
    // refreshAuthTokenService.checkAndRefreshToken();

    const headers = super.getHeaders();
    const access_token = super.getToken();

    if (!access_token) {
      throw new Error('Não foi encontrado o @access_token');
    }

    const validateResponse = await customersAuthorizationApi.post(
      `${ECustomerAuthorizationEndpoints.VALIDATE}`,
      { access_token },
      { headers }
    );

    try {
      const { id, email, roles } = validateResponse.data.user;

      const uniqueCustomersIds = [
        ...(new Set(
          Object.keys(roles)
            .map((key) => key.toLowerCase().includes('customers') && roles[key])
            .flat()
            .filter(Boolean)
        ) as unknown as Array<string>)
      ];

      const formattedObject = {
        id: id,
        email: email,
        customersIds: uniqueCustomersIds
      };

      const userDataEncoded = btoa(JSON.stringify(formattedObject));

      // ADCL-4810
      // document.cookie = `oldTagData=${userDataEncoded}`;
    } catch (err) {
      console.error(err);
    }

    const user: IUser = {
      ...validateResponse.data.user,
      expecialSteel: validateResponse.data.expecialSteel
    };

    return user;
  }

  async getUser(userId: string): Promise<IUser> {
    const headers = super.getHeaders();
    const userResponse = await customersAuthorizationApi.get(
      `${ECustomerAuthorizationEndpoints.USERS}/${userId}`,
      { headers }
    );

    const user: IUser = userResponse.data.user;
    if (!user.searchCustomerType) {
      // user = await this.saveUser(user, undefined, undefined, undefined, undefined, ECustomerTypeView.EMITTER);
      user.searchCustomerType = ECustomerTypeView.EMITTER;
    }
    return user;
  }

  async saveUser(
    user: IUser,
    userPreference?: IUserPreference,
    sendOrdersToEmail?: boolean,
    sendFinancialToEmail?: boolean,
    userAcceptNotification?: boolean,
    searchCustomerType?: ECustomerTypeView,
    improvementResearch?: boolean,
    privacyTerms?: boolean,
    userName?: string,
    improvementResearchNps?: EResearchNps,
    jorneySurveyShowcaseStatus?: EResearchNps,
    jorneySurveyHistoryStatus?: EResearchNps,
    jorneySurveyItemsStatus?: EResearchNps,
    jorneySurveySheetStatus?: EResearchNps,
    gerdauProModal?: boolean
  ): Promise<IUser> {
    const headers = super.getHeaders();

    const userResponse = await customersAuthorizationApi.patch(
      `${ECustomerAuthorizationEndpoints.USERS}/${user.id}`,
      {
        user: {
          name: userName,
          preferences: userPreference ? [userPreference] : undefined,
          sendOrdersToEmail,
          sendFinancialToEmail,
          searchCustomerType,
          userAcceptNotification,
          ...([
            improvementResearchNps,
            jorneySurveyShowcaseStatus,
            jorneySurveyHistoryStatus,
            jorneySurveyItemsStatus,
            jorneySurveySheetStatus
          ].length > 0 && {
            userSurveys: {
              ...(improvementResearchNps && {
                userNps: {
                  improvementSurveyNps: improvementResearchNps
                }
              }),
              ...(jorneySurveyShowcaseStatus && {
                newJorneySurveyShowcase: {
                  status: jorneySurveyShowcaseStatus
                }
              }),
              ...(jorneySurveyHistoryStatus && {
                newJorneySurveyHistory: {
                  status: jorneySurveyHistoryStatus
                }
              }),
              ...(jorneySurveyItemsStatus && {
                newJorneySurveyItems: {
                  status: jorneySurveyItemsStatus
                }
              }),
              ...(jorneySurveySheetStatus && {
                newJorneySurveySheet: {
                  status: jorneySurveySheetStatus
                }
              })
            }
          }),
          ...([privacyTerms, improvementResearch, gerdauProModal].filter(
            (term) => term
          ).length > 0 && {
            terms: {
              privacyTerms,
              improvementResearch,
              gerdauProModal
            }
          })
        }
      },
      { headers }
    );

    const newUser: IUser = userResponse.data.user;

    return newUser;
  }

  async createPassword(
    userId: string,
    password: string,
    userAcceptNotification: boolean
  ) {
    const headers = this.getHeaders();

    const bodyRequest = {
      firstAuthentication: userId,
      password,
      userAcceptNotification
      // terms: {
      //     userAcceptNotification,
      // },
    };

    try {
      await customersAuthorizationApi.post(
        ECustomerAuthorizationEndpoints.CREATE_PASSWORD,
        { ...bodyRequest },
        { headers }
      );
    } catch (err) {
      console.error(err);
      throw new Error('Não foi possível criar a senha.');
    }
  }

  async userLogout() {
    const ssoAccess = super.getSsoAccess();
    ssoAccess ? await this.userLogoutAzure() : await this.userLogoutOkta();
  }

  async userLogoutOkta() {
    const headers = super.getHeaders();
    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.LOGOUT,
      { token: super.getToken() },
      { headers }
    );
    super.logout();
  }

  async userLogoutAzure() {
    super.logout();
  }

  static async sendNPS(
    rating?: string,
    mhsRating?: string,
    npsText?: string | undefined
  ) {
    const headers = new UserService().getHeaders();

    const body = {
      improvementSurveyNps: 'answered',
      npsRating: rating ?? '',
      npsText: npsText ?? '',
      mhsRating: mhsRating ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_NPS,
      body,
      { headers }
    );
  }
  static async sendSurveySheet({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SHEET,
      body,
      { headers }
    );
  }
  static async sendSurveySelectingItems({
    status,
    rating,
    surveyText
  }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SELECTING_ITEMS,
      body,
      { headers }
    );
  }
  static async sendSurveyHistory({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_HISTORY,
      body,
      { headers }
    );
  }
  static async sendSurveyShowCase({ status, rating, surveyText }: SurveyProps) {
    const headers = new UserService().getHeaders();

    const body = {
      status,
      surveyRating: rating ?? '',
      surveyText: surveyText ?? ''
    };

    await customersAuthorizationApi.post(
      ECustomerAuthorizationEndpoints.SEND_JORNEY_SHOWCASE,
      body,
      { headers }
    );
  }
}
